<template>
  <SoAlert
    v-if="noCurrentTariff"
    data-testid="current-tariff-error-alert"
    class="mb-3"
  >
    There are currently no active agreements on this account. To backdate a
    tariff, you may need to speak to a Senior or Operations Specialist.
  </SoAlert>
  <SoAlert v-if="hasExportMeter" type="info" class="mb-3">
    This renewal will impact import tariffs only.
  </SoAlert>
  <SoDataTable
    :headers="renewals.quoteTableData.headers"
    :items="renewals.quoteTableData.items"
    :has-pagination="false"
    :searchable="false"
    show-copy-to-clipboard
    data-testid="renewals-quote-table"
  />
  <div class="quote-layout-grid align-center my-2">
    <div>Tariff Change Date</div>
    <div>
      <SoDatePicker
        v-model="renewals.changeDate"
        label="Auto-aligned to earliest agreement end date"
        :min-date="renewals.minRenewalDate"
        :max-date="renewals.maxRenewalDate"
      />
      <p v-if="renewals.agreementEndDates.length > 1" class="text-danger">
        Different agreement end dates
        {{ differentAgreementEndDates }}
      </p>
      <p
        v-if="
          renewals.agreementEndDates?.length > 0 &&
          !renewals.defaultRenewalStart?.date.isSame(
            renewals.changeDate,
            'day',
          ) &&
          renewals.closestAgreementEndDate !== null
        "
        class="text-danger"
      >
        Second agreement ends on
        {{ renewals.defaultRenewalStartFormatted }}
      </p>
    </div>
    <div class="d-flex align-center">
      Exit Fees ({{ formatMoney({ amount: renewals.exitFeeTotal }) }})
      <SoTooltip :text="exitFeeTooltip">
        <SoIcon
          icon="info"
          class="ml-2"
          color="info"
          data-testid="exit-fee-info"
        />
      </SoTooltip>
    </div>
    <!-- SO-15468: it's a business requirement to keep this disabled -->
    <SoCheckbox v-model="renewals.includeExitFee" disabled />
    <div>
      Inc. Account Balance ({{
        formatMoney({
          amount: current.account.data.balance,
          showNegativeSign: false,
        })
      }}
      {{ current.account.data.balanceStatus.text }})
    </div>
    <SoCheckbox v-model="renewals.includeAccountBalance" />
    <div>One-off payment (optional)</div>
    <SoTextField v-model="renewals.oneOffPayment" prefix="£" type="number" />
  </div>
  <v-divider />
  <div class="quote-layout-grid my-2">
    <div class="font-weight-bold">Adjusted Annual Cost</div>
    <div>
      <p :class="{ 'text-danger': renewals.adjustedAnnualCost < 0 }">
        {{
          formatMoney({
            amount: renewals.adjustedAnnualCost,
            showNegativeSign: false,
          })
        }}
        <span v-if="renewals.tariffCostsDifference < 0" class="text-success">
          ↓{{
            formatMoney({
              amount: renewals.tariffCostsDifference,
              showNegativeSign: false,
            })
          }}
        </span>
        <span v-if="renewals.tariffCostsDifference > 0" class="text-danger">
          ↑{{
            formatMoney({
              amount: renewals.tariffCostsDifference,
              showNegativeSign: false,
            })
          }}
        </span>
      </p>
    </div>
    <div class="font-weight-bold">Current Payments</div>
    <div v-if="current.currentPaymentSchedule.data">
      {{ renewals.currentPayments }}
    </div>
    <i v-else>No active payment schedules</i>
    <div class="font-weight-bold">Suggested Payments</div>
    <div>
      <div>
        <span class="font-weight-bold">Equal:</span>
        {{
          formatMoney({
            amount: renewals.suggestedPaymentAmount,
            showNegativeSign: false,
            round: true,
          })
        }}
        <span class="font-italic"
          >(exact
          {{
            formatMoney({
              amount: renewals.suggestedPaymentAmount,
              showNegativeSign: false,
            })
          }})
        </span>
      </div>
      <div>
        <span class="font-weight-bold">Seasonal: </span>
        {{
          formatMoney({
            amount: Math.round(renewals.suggestedPaymentAmount) * 0.75,
            showNegativeSign: false,
          })
        }}
        Summer,
        {{
          formatMoney({
            amount: Math.round(renewals.suggestedPaymentAmount) * 1.25,
            showNegativeSign: false,
          })
        }}
        Winter
      </div>
      <template v-if="current.currentPaymentSchedule.data">
        <div>Suggested Vs Current Difference</div>
        <div>
          <span v-if="renewals.paymentChangeDiff < 0" class="text-success">
            ↓{{
              formatMoney({
                amount: renewals.paymentChangeDiff,
                showNegativeSign: false,
              })
            }}
          </span>
          <span v-else-if="renewals.paymentChangeDiff > 0" class="text-danger">
            ↑{{
              formatMoney({
                amount: renewals.paymentChangeDiff,
                showNegativeSign: false,
              })
            }}
          </span>
          <span v-else>
            {{
              formatMoney({
                amount: renewals.paymentChangeDiff,
                showNegativeSign: false,
              })
            }}
          </span>
          <span class="font-italic">
            ({{
              formatMoney({
                amount: Math.round(renewals.suggestedPaymentAmount),
                showNegativeSign: false,
              })
            }}
            -
            {{
              formatMoney({
                amount: renewals.paymentChangeDiff,
                showNegativeSign: false,
              })
            }})
          </span>
        </div>
      </template>
    </div>
  </div>
  <v-divider />
  <div class="d-flex mt-2">
    <SoButton
      :disabled="sameTariffCodes"
      title="Generate Quote"
      @click="isQuoteModalOpen = true"
    />
    <QuoteModal v-model="isQuoteModalOpen" />
    <SoButton
      :disabled="sameTariffCodes"
      class="ml-auto"
      title="Change tariff & payments"
      icon="chevron-right"
      data-testid="change-tariff-payments"
      @click="isRenewalSummaryModalOpen = true"
    />
  </div>
  <RenewalSummaryModal
    v-if="isRenewalSummaryModalOpen"
    v-model="isRenewalSummaryModalOpen"
  />
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { useRenewalsStore } from "@/stores/renewals"
  import { formatMoney } from "@/helpers/moneyHelpers"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import SoDatePicker from "@/components/formControls/SoDatePicker.vue"
  import SoIcon from "@/components/SoIcon.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoTooltip from "@/components/SoTooltip.vue"
  import QuoteModal from "../components/QuoteModal.vue"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import RenewalSummaryModal from "../modals/RenewalSummaryModal.vue"
  import dayjs from "dayjs"
  import SoAlert from "@/components/SoAlert.vue"
  import { FeaturesService } from "@/services/admin/FeaturesService"
  import { renewalDateFormat } from "@/helpers/dateHelper"

  export default defineComponent({
    components: {
      SoAlert,
      SoButton,
      SoTextField,
      SoCheckbox,
      SoDatePicker,
      SoTooltip,
      SoIcon,
      QuoteModal,
      SoDataTable,
      RenewalSummaryModal,
    },
    data() {
      return {
        renewals: useRenewalsStore(),
        current: useCurrentAccountStore(),
        editChangeDate: false,
        isQuoteModalOpen: false,
        formatMoney,
        isRenewalSummaryModalOpen: false,
        dayjs: dayjs,
      }
    },
    computed: {
      showElecVatRateChangeInfo(): boolean {
        return (
          FeaturesService.isFeatureActive("SHOW_ELEC_VAT_RATE_CHANGE_INFO") ??
          false
        )
      },
      exitFeeTooltip(): string {
        const base =
          "Amount is calculated using the agreement end dates & renewal date selected."
        return this.showElecVatRateChangeInfo
          ? `${base} Exit fees are applied on the next bill at the VAT rate in effect on the bill issue date.`
          : base
      },
      noCurrentTariff() {
        const meterpointsTariffs = Object.values(
          this.renewals.meterpointTariffs,
        )
        return meterpointsTariffs.some(
          (meterpointTariff) =>
            !meterpointTariff.currentTariff &&
            !meterpointTariff.meterpoint.isExport,
        )
      },
      hasExportMeter() {
        const meterpointsTariffs = Object.values(
          this.renewals.meterpointTariffs,
        )
        return meterpointsTariffs.some(
          (meterpointTariff) => meterpointTariff.meterpoint.isExport,
        )
      },
      currentTariffCodes() {
        return Object.values(this.renewals.meterpointTariffs).map(
          (tariff) => tariff.currentTariff?.code,
        )
      },
      selectedTariffCodes() {
        return Object.values(this.renewals.selectedTariffs).map(
          (tariff) => tariff.code,
        )
      },
      sameTariffCodes() {
        return (
          this.currentTariffCodes.toString() ===
          this.selectedTariffCodes.toString()
        )
      },
      differentAgreementEndDates() {
        return this.renewals.agreementEndDates
          .map((date) => date.format(renewalDateFormat))
          .join(", ")
      },
    },
  })
</script>

<style scoped>
  .quote-layout-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-gap: 0.5rem;
  }

  .change-date-button {
    color: rgb(var(--v-theme-info));
  }
</style>
